<template>
  <div
    class="create-ad-wizard"
    :class="{ 'create-ad-wizard--embedded': embedded }"
  >
    <div class="create-ad-panel">
      <aside class="create-ad-sidebar">
        <div class="sidebar-header">
          <h1>{{ isEditMode ? 'Edit Ad' : 'Create New Ad' }} <span class="sparkle" aria-hidden="true">✨</span></h1>
          <p>Fill in the details to create and publish your ad.</p>
        </div>

        <div
          class="sidebar-illustration"
          aria-hidden="true"
        >
          <div class="illus-card">
            <span class="illus-ad-label">AD</span>
            <span class="illus-plus">+</span>
          </div>
        </div>

        <ol class="create-ad-stepper">
          <li
            v-for="step in steps"
            :key="step.id"
            class="stepper-item"
            :class="{
              'is-active': activeStep === step.id,
              'is-complete': activeStep > step.id,
              'is-clickable': step.id <= activeStep,
              'is-locked': step.id > activeStep,
            }"
            @click="goToStep(step.id)"
          >
            <span class="stepper-marker">{{ step.id }}</span>
            <div class="stepper-copy">
              <strong>{{ step.title }}</strong>
              <span>{{ step.description }}</span>
            </div>
          </li>
        </ol>
      </aside>

      <div class="create-ad-main">
        <div
          v-if="isLoadingAd"
          class="d-flex justify-content-center py-5"
        >
          <b-spinner label="Loading ad..." />
        </div>

        <validation-observer
          v-else
          ref="createAdForm"
          class="create-ad-form"
        >
          <section
            v-show="activeStep === 1"
            class="form-section"
          >
            <div class="section-title-row">
              <span class="section-icon-badge">
                <feather-icon
                  icon="FileTextIcon"
                  size="16"
                />
              </span>
              <h3>1. Basic Information</h3>
            </div>

            <label class="field-label">Title *</label>
            <b-form-input
              v-model="adForm.title"
              class="form-field"
              :class="{ 'is-invalid': basicStepErrors.title }"
              placeholder="Enter ad title"
            />
            <p
              v-if="basicStepErrors.title"
              class="field-error"
            >
              {{ basicStepErrors.title }}
            </p>

            <label class="field-label">Description *</label>
            <b-form-input
              v-model="adForm.description"
              class="form-field"
              :class="{ 'is-invalid': basicStepErrors.description }"
              placeholder="Enter ad description"
            />
            <p
              v-if="basicStepErrors.description"
              class="field-error"
            >
              {{ basicStepErrors.description }}
            </p>

            <div>
              <label class="field-label">Select Ad Type *</label>
              <b-form-select
                v-model="adForm.adType"
                :options="adTypeOptions"
                class="form-field"
              />
            </div>

            <label class="field-label">Brand *</label>
            <b-form-select
              v-model="adForm.brandId"
              :options="brandSelectOptions"
              class="form-field"
              :class="{ 'is-invalid': basicStepErrors.brandId }"
            />
            <p
              v-if="basicStepErrors.brandId"
              class="field-error"
            >
              {{ basicStepErrors.brandId }}
            </p>
            <p
              v-if="!brandOptions.length"
              class="field-hint"
            >
              Create a brand before you submit an ad.
              <router-link :to="{ name: 'brands' }">Manage brands</router-link>
            </p>

            <label class="field-label">Primary Category *</label>
            <b-form-select
              v-model="adForm.categoryId"
              :options="categorySelectOptions"
              class="form-field"
              :class="{ 'is-invalid': basicStepErrors.categoryId }"
              @change="onPrimaryCategoryChange"
            />
            <p
              v-if="basicStepErrors.categoryId"
              class="field-error"
            >
              {{ basicStepErrors.categoryId }}
            </p>

            <div
              v-if="showCategorySuggestForm"
              class="category-suggest-block"
            >
              <p class="field-hint">
                Suggest a new category for admin review. After you send it, pick an existing primary category so you can continue creating this ad.
              </p>
              <label class="field-label">Suggested category name *</label>
              <b-form-input
                v-model="suggestedCategoryName"
                class="form-field"
                :class="{ 'is-invalid': basicStepErrors.suggestedCategoryName }"
                placeholder="e.g. Organic skincare"
              />
              <p
                v-if="basicStepErrors.suggestedCategoryName"
                class="field-error"
              >
                {{ basicStepErrors.suggestedCategoryName }}
              </p>
              <label class="field-label">Under existing parent (optional)</label>
              <b-form-select
                v-model="suggestedCategoryParentId"
                :options="suggestParentSelectOptions"
                class="form-field"
              />
              <b-button
                variant="outline-primary"
                class="mt-1"
                :disabled="isSubmittingCategorySuggestion"
                @click="submitCategorySuggestion"
              >
                <b-spinner
                  v-if="isSubmittingCategorySuggestion"
                  small
                  class="mr-50"
                />
                Send suggestion to admin
              </b-button>
            </div>

            <label class="field-label">Secondary Category</label>
            <b-form-select
              v-model="adForm.secondaryCategoryId"
              :options="secondaryCategorySelectOptions"
              class="form-field"
              :disabled="!adForm.categoryId || isSuggestCategorySelected"
              :class="{ 'is-invalid': basicStepErrors.secondaryCategoryId }"
            />
            <p
              v-if="basicStepErrors.secondaryCategoryId"
              class="field-error"
            >
              {{ basicStepErrors.secondaryCategoryId }}
            </p>

            <label class="field-label">Age Restriction *</label>
            <b-form-select
              v-model="adForm.minAge"
              :options="ageRestrictionOptions"
              class="form-field"
              :class="{ 'is-invalid': basicStepErrors.minAge }"
            />
            <p
              v-if="basicStepErrors.minAge"
              class="field-error"
            >
              {{ basicStepErrors.minAge }}
            </p>
            <p class="field-hint">
              Required. Choose “No age restriction” or the minimum viewer age (e.g. 21+ for alcohol).
            </p>

            <label class="field-label">Audience targeting</label>
            <p class="field-hint">
              Optional. We match viewers privately — you never see anyone’s demographic data.
            </p>
            <b-form-select
              v-model="adForm.targetGender"
              :options="targetGenderOptions"
              class="form-field"
            />
            <div class="field-row">
              <div>
                <label class="field-label">Target age min</label>
                <b-form-input
                  v-model="adForm.targetAgeMin"
                  type="number"
                  min="13"
                  max="100"
                  placeholder="Optional"
                  class="form-field"
                />
              </div>
              <div>
                <label class="field-label">Target age max</label>
                <b-form-input
                  v-model="adForm.targetAgeMax"
                  type="number"
                  min="13"
                  max="100"
                  placeholder="Optional"
                  class="form-field"
                />
              </div>
            </div>
          </section>

          <section
            v-show="activeStep === 2"
            class="form-section"
          >
            <div class="section-title-row">
              <span class="section-icon-badge">
                <feather-icon
                  icon="UploadCloudIcon"
                  size="16"
                />
              </span>
              <h3>2. Media Upload</h3>
            </div>

            <p
              v-if="mediaError"
              class="field-error mb-1"
            >
              {{ mediaError }}
            </p>
            <div
              v-if="existingMediaUrls.length"
              class="existing-media-row mb-1"
            >
              <div
                v-for="(url, idx) in existingMediaUrls"
                :key="`${url}-${idx}`"
                class="existing-media-item"
              >
                <video
                  v-if="isVideoMediaUrl(url)"
                  :src="resolveMediaUrl(url)"
                  class="existing-media-thumb"
                  muted
                  playsinline
                />
                <img
                  v-else
                  :src="resolveMediaUrl(url)"
                  alt="Existing ad media"
                  class="existing-media-thumb"
                >
                <button
                  type="button"
                  class="existing-media-remove"
                  @click="removeExistingMedia(idx)"
                >
                  Remove
                </button>
              </div>
            </div>
            <ad-media-drop-zone
              v-model="adForm.mediaFiles"
              :ad-type="adForm.adType"
              class="create-ad-drop-zone"
              @format-error="onMediaFormatError"
            />
            <p
              v-if="mediaStepErrors.media"
              class="field-error"
            >
              {{ mediaStepErrors.media }}
            </p>
          </section>

          <section
            v-show="activeStep === 3"
            class="form-section"
          >
            <div class="section-title-row">
              <span class="section-icon-badge">
                <feather-icon
                  icon="MousePointerIcon"
                  size="16"
                />
              </span>
              <h3>3. Call to Action</h3>
            </div>

            <div class="field-row">
              <div>
                <label class="field-label">CTA Button Text</label>
                <div
                  class="input-with-icon"
                  :class="{ 'is-invalid': ctaStepErrors.ctaText }"
                >
                  <span class="input-icon">
                    <feather-icon
                      icon="TypeIcon"
                      size="16"
                    />
                  </span>
                  <b-form-input
                    v-model="adForm.ctaText"
                    placeholder="Enter CTA button text"
                  />
                </div>
                <p
                  v-if="ctaStepErrors.ctaText"
                  class="field-error"
                >
                  {{ ctaStepErrors.ctaText }}
                </p>
              </div>
              <div>
                <label class="field-label">Destination URL</label>
                <div
                  class="input-with-icon"
                  :class="{ 'is-invalid': ctaStepErrors.destinationUrl }"
                >
                  <span class="input-icon">
                    <feather-icon
                      icon="LinkIcon"
                      size="16"
                    />
                  </span>
                  <b-form-input
                    v-model="adForm.destinationUrl"
                    placeholder="Enter destination URL"
                    @blur="onDestinationUrlBlur"
                  />
                </div>
                <p
                  v-if="ctaStepErrors.destinationUrl"
                  class="field-error"
                >
                  {{ ctaStepErrors.destinationUrl }}
                </p>
              </div>
            </div>
          </section>

          <section
            v-show="activeStep === 4"
            class="form-section"
          >
            <div class="section-title-row">
              <span class="section-icon-badge">
                <feather-icon
                  icon="CalendarIcon"
                  size="16"
                />
              </span>
              <h3>4. Schedule Date</h3>
            </div>

            <div class="schedule-summary-bar">
              {{ scheduleSummaryText }}
            </div>

            <div class="field-row">
              <div
                class="time-date-field"
                :class="{ 'is-invalid': scheduleFieldErrors.startTime }"
              >
                <label>Start Time</label>
                <div class="picker-input-wrap native-picker-field">
                  <b-form-input
                    v-model="scheduleForm.startTime"
                    type="time"
                    :class="{ 'is-invalid': scheduleFieldErrors.startTime }"
                    @input="onStartTimeChange"
                  />
                </div>
                <p
                  v-if="scheduleFieldErrors.startTime"
                  class="field-error"
                >
                  {{ scheduleFieldErrors.startTime }}
                </p>
              </div>
              <div
                class="time-date-field"
                :class="{ 'is-invalid': scheduleFieldErrors.endTime }"
              >
                <label>End Time</label>
                <div class="picker-input-wrap native-picker-field">
                  <b-form-input
                    v-model="scheduleForm.endTime"
                    type="time"
                    :min="minEndTime"
                    :class="{ 'is-invalid': scheduleFieldErrors.endTime }"
                    @input="onEndTimeChange"
                  />
                </div>
                <p
                  v-if="scheduleFieldErrors.endTime"
                  class="field-error"
                >
                  {{ scheduleFieldErrors.endTime }}
                </p>
              </div>
            </div>

            <div class="field-row">
              <div
                class="time-date-field"
                :class="{ 'is-invalid': scheduleFieldErrors.startDate }"
              >
                <label>Start Date</label>
                <div class="picker-input-wrap native-picker-field">
                  <b-form-input
                    v-model="scheduleForm.startDate"
                    type="date"
                    :class="{ 'is-invalid': scheduleFieldErrors.startDate }"
                    @input="onStartDateChange"
                  />
                </div>
                <p
                  v-if="scheduleFieldErrors.startDate"
                  class="field-error"
                >
                  {{ scheduleFieldErrors.startDate }}
                </p>
              </div>
              <div
                class="time-date-field"
                :class="{ 'is-invalid': scheduleFieldErrors.endDate || scheduleFieldErrors.scheduleRange }"
              >
                <label>End Date</label>
                <div class="picker-input-wrap native-picker-field">
                  <b-form-input
                    v-model="scheduleForm.endDate"
                    type="date"
                    :min="minEndDate"
                    :class="{ 'is-invalid': scheduleFieldErrors.endDate || scheduleFieldErrors.scheduleRange }"
                    @input="onEndDateChange"
                  />
                </div>
                <p
                  v-if="scheduleFieldErrors.endDate"
                  class="field-error"
                >
                  {{ scheduleFieldErrors.endDate }}
                </p>
                <p
                  v-else-if="scheduleFieldErrors.scheduleRange"
                  class="field-error"
                >
                  {{ scheduleFieldErrors.scheduleRange }}
                </p>
              </div>
            </div>
          </section>

          <div
            v-if="activeStep === 1"
            class="form-footer form-footer--end"
          >
            <b-button
              class="next-btn"
              @click="goToNextStep"
            >
              Next
              <feather-icon
                icon="ArrowRightIcon"
                size="16"
                class="btn-trailing-icon"
              />
            </b-button>
          </div>

          <div
            v-else-if="activeStep === 2 || activeStep === 3"
            class="form-footer form-footer--split"
          >
            <b-button
              variant="outline-secondary"
              class="back-btn"
              @click="goToPreviousStep"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="16"
                class="btn-leading-icon"
              />
              Back
            </b-button>
            <b-button
              class="next-btn"
              @click="goToNextStep"
            >
              Next
              <feather-icon
                icon="ArrowRightIcon"
                size="16"
                class="btn-trailing-icon"
              />
            </b-button>
          </div>

          <div
            v-else
            class="form-footer form-footer--three"
          >
            <b-button
              variant="outline-secondary"
              class="back-btn"
              @click="goToPreviousStep"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="16"
                class="btn-leading-icon"
              />
              Back
            </b-button>
            <b-button
              variant="outline-secondary"
              class="cancel-btn"
              @click="onCancel"
            >
              Cancel
            </b-button>
            <b-button
              class="submit-btn"
              :disabled="submittingCreateAd"
              @click="submitCreateAd"
            >
              <feather-icon
                icon="SendIcon"
                size="16"
                class="submit-icon"
              />
              {{ submittingCreateAd ? 'Submitting...' : 'Submit for Approval' }}
              <b-spinner
                v-if="submittingCreateAd"
                small
                class="ml-50"
                label="Submitting"
              />
            </b-button>
          </div>
        </validation-observer>
      </div>
    </div>

    <vendor-outcome-modal
      v-if="!embedded"
      :visible.sync="showOutcomeModal"
      variant="ad-submitted"
      @primary="onCreateAdOutcomePrimary"
    />
  </div>
</template>

<script>
import {
  BFormInput,
  BFormSelect,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import {
  validateScheduleRange,
  validateDestinationUrl,
  extractApiErrorMessage,
  parseApiDateTime,
  buildClientRequestId,
  getAdMediaFormatError,
  isVideoMediaUrl,
  resolveExistingAdMediaUrls,
} from '@/utils/adCreate'
import {
  fetchAdCategories,
  primaryCategoryOptions,
  secondaryCategoryOptions,
  CATEGORY_SUGGEST_VALUE,
  isCategorySuggestValue,
} from '@/utils/adCategories'
import { notifyInfo, notifyError } from '@/utils/uiActions'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import AdMediaDropZone from '@/features/ads/components/AdMediaDropZone.vue'
import VendorOutcomeModal from '@/components/vendor/VendorOutcomeModal.vue'

const CREATE_AD_STEPS = [
  { id: 1, title: 'Basic Information', description: 'Provide ad details' },
  { id: 2, title: 'Media Upload', description: 'Add images or videos' },
  { id: 3, title: 'Call to Action', description: 'Set action and destination' },
  { id: 4, title: 'Schedule Date', description: 'Set upload & expiry date' },
]

const AGE_RESTRICTION_OPTIONS = [
  { value: null, text: 'Select age restriction' },
  { value: 0, text: 'No age restriction' },
  { value: 13, text: '13 years and older' },
  { value: 16, text: '16 years and older' },
  { value: 18, text: '18 years and older' },
  { value: 21, text: '21 years and older' },
]

const TARGET_GENDER_OPTIONS = [
  { value: 'any', text: 'All genders' },
  { value: 'female', text: 'Female' },
  { value: 'male', text: 'Male' },
  { value: 'non_binary', text: 'Non-binary' },
]

export default {
  name: 'CreateAdWizard',
  components: {
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    AdMediaDropZone,
    VendorOutcomeModal,
    ValidationObserver,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    editAdId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      showOutcomeModal: false,
      editAdId: null,
      existingMediaUrls: [],
      isLoadingAd: false,
      activeStep: 1,
      steps: CREATE_AD_STEPS,
      adForm: {
        title: '',
        description: '',
        adType: 'Image',
        brandId: null,
        categoryId: null,
        secondaryCategoryId: null,
        minAge: null,
        targetGender: 'any',
        targetAgeMin: '',
        targetAgeMax: '',
        ctaText: '',
        destinationUrl: '',
        mediaFiles: [],
      },
      categoryOptions: [],
      suggestedCategoryName: '',
      suggestedCategoryParentId: null,
      isSubmittingCategorySuggestion: false,
      /** Explicit flag so the suggest UI shows even if select coerces the sentinel value. */
      showCategorySuggestForm: false,
      brandOptions: [],
      ageRestrictionOptions: AGE_RESTRICTION_OPTIONS,
      targetGenderOptions: TARGET_GENDER_OPTIONS,
      scheduleForm: {
        startTime: '',
        endTime: '',
        startDate: '',
        endDate: '',
      },
      adTypeOptions: ['Image', 'Video'],
      submittingCreateAd: false,
      showBasicValidation: false,
      showMediaValidation: false,
      showCtaValidation: false,
      destinationUrlTouched: false,
      showScheduleValidation: false,
      mediaFormatError: '',
    }
  },
  computed: {
    isEditMode() {
      return Boolean(this.editAdId)
    },
    brandSelectOptions() {
      return [
        { value: null, text: 'Select brand' },
        ...this.brandOptions.map(brand => ({
          value: brand.id,
          text: brand.name,
        })),
      ]
    },
    isSuggestCategorySelected() {
      return this.showCategorySuggestForm || isCategorySuggestValue(this.adForm.categoryId)
    },
    categorySelectOptions() {
      return [
        { value: null, text: 'Select primary category' },
        ...primaryCategoryOptions(this.categoryOptions),
        { value: CATEGORY_SUGGEST_VALUE, text: 'Other / Suggest a Category' },
      ]
    },
    suggestParentSelectOptions() {
      return [
        { value: null, text: 'No parent (top-level category)' },
        ...primaryCategoryOptions(this.categoryOptions).map(item => ({
          value: item.value,
          text: item.text,
        })),
      ]
    },
    secondaryCategorySelectOptions() {
      if (!this.adForm.categoryId) {
        return [{ value: null, text: 'Select primary category first' }]
      }
      const children = secondaryCategoryOptions(this.categoryOptions, this.adForm.categoryId)
      if (!children.length) {
        return [{ value: null, text: 'No child categories for this primary' }]
      }
      return [
        { value: null, text: 'None (optional)' },
        ...children,
      ]
    },
    scheduleSummaryText() {
      const hasStart = this.scheduleForm.startDate || this.scheduleForm.startTime
      const hasEnd = this.scheduleForm.endDate || this.scheduleForm.endTime
      if (!hasStart && !hasEnd) {
        return 'Select start and end date & time'
      }
      const start = `${this.formatTimeAmPm(this.scheduleForm.startTime)} | ${this.formatDateLong(this.scheduleForm.startDate)}`
      const end = `${this.formatTimeAmPm(this.scheduleForm.endTime)} | ${this.formatDateLong(this.scheduleForm.endDate)}`
      return `${start}  →  ${end}`
    },
    mediaError() {
      return this.mediaFormatError
    },
    basicStepErrors() {
      if (!this.showBasicValidation) return {}
      return this.buildBasicStepErrors()
    },
    mediaStepErrors() {
      if (!this.showMediaValidation) return {}
      const errors = this.buildMediaStepErrors()
      if (this.mediaFormatError) errors.media = this.mediaFormatError
      return errors
    },
    ctaStepErrors() {
      if (!this.showCtaValidation && !this.destinationUrlTouched) return {}
      return this.buildCtaStepErrors()
    },
    scheduleFieldErrors() {
      if (!this.showScheduleValidation) return {}
      return this.buildScheduleFieldErrors()
    },
    hasScheduleErrors() {
      return Object.keys(this.scheduleFieldErrors).length > 0
    },
    minEndDate() {
      return this.scheduleForm.startDate || ''
    },
    minEndTime() {
      const { startDate, endDate, startTime } = this.scheduleForm
      if (startDate && endDate && startDate === endDate) {
        return startTime || ''
      }
      return ''
    },
  },
    async mounted() {
    try {
      this.categoryOptions = await fetchAdCategories()
    } catch {
      this.categoryOptions = []
    }
    await this.loadBrands()
    const adId = this.editAdId || (!this.embedded ? this.$route.params.id : null)
    if (adId) {
      await this.loadAdForEdit(adId)
    }
  },
  methods: {
    isVideoMediaUrl,
    resolveMediaUrl,
    async loadBrands() {
      try {
        const response = await vendorPanelApi.brands({ active_only: 1 })
        const list = response?.data?.data?.brands || []
        this.brandOptions = list
          .filter(brand => brand && brand.id)
          .map(brand => ({
            id: Number(brand.id),
            name: brand.name || `Brand #${brand.id}`,
          }))
        if (!this.adForm.brandId && this.brandOptions.length === 1) {
          this.adForm.brandId = this.brandOptions[0].id
        }
      } catch {
        this.brandOptions = []
      }
    },
    onPrimaryCategoryChange() {
      // Secondary must always belong to the newly selected primary parent.
      this.adForm.secondaryCategoryId = null
      const suggesting = isCategorySuggestValue(this.adForm.categoryId)
      this.showCategorySuggestForm = suggesting
      if (!suggesting) {
        this.suggestedCategoryName = ''
        this.suggestedCategoryParentId = null
      }
    },
    async submitCategorySuggestion() {
      const name = String(this.suggestedCategoryName || '').trim()
      if (!name || this.isSubmittingCategorySuggestion) {
        if (!name) {
          this.showBasicValidation = true
        }
        return
      }
      this.isSubmittingCategorySuggestion = true
      try {
        const payload = { name }
        const parentId = Number(this.suggestedCategoryParentId || 0)
        if (Number.isFinite(parentId) && parentId > 0) {
          payload.parent_id = parentId
        }
        await vendorPanelApi.requestCategory(payload)
        notifyInfo(this, 'Category suggestion sent to admin for approval. Now select an existing primary category for this ad.')
        this.suggestedCategoryName = ''
        this.suggestedCategoryParentId = null
        this.showCategorySuggestForm = false
        this.adForm.categoryId = null
      } catch (error) {
        notifyError(this, extractApiErrorMessage(error, 'Unable to submit category suggestion'))
      } finally {
        this.isSubmittingCategorySuggestion = false
      }
    },
    removeExistingMedia(idx) {
      this.existingMediaUrls = this.existingMediaUrls.filter((_, i) => i !== idx)
    },
    hasAdMedia() {
      return (Array.isArray(this.adForm.mediaFiles) && this.adForm.mediaFiles.length > 0)
        || (Array.isArray(this.existingMediaUrls) && this.existingMediaUrls.length > 0)
    },
    async loadAdForEdit(adId) {
      this.isLoadingAd = true
      this.editAdId = adId
      try {
        const response = await vendorPanelApi.adsDetailById({ id: adId })
        const data = response?.data?.data || {}
        this.adForm.title = data.title || ''
        this.adForm.description = data.description || ''
        this.adForm.ctaText = data.cta_text || ''
        this.adForm.destinationUrl = data.cta_link || ''
        this.adForm.adType = String(data.ad_type || data.adType || 'image').toLowerCase() === 'video' ? 'Video' : 'Image'
        this.adForm.brandId = data.brand_id || data.brand?.id || null
        this.adForm.categoryId = data.category_id || data.category?.id || null
        this.adForm.secondaryCategoryId = data.secondary_category_id || data.secondary_category?.id || null
        this.adForm.minAge = data.min_age == null ? 0 : Number(data.min_age)
        this.adForm.targetGender = data.target_gender || 'any'
        this.adForm.targetAgeMin = data.target_age_min == null ? '' : Number(data.target_age_min)
        this.adForm.targetAgeMax = data.target_age_max == null ? '' : Number(data.target_age_max)
        this.existingMediaUrls = resolveExistingAdMediaUrls(data)
        const publish = parseApiDateTime(data.dates?.publish_at)
        const expire = parseApiDateTime(data.expires_at || data.dates?.expire_at)
        this.scheduleForm.startDate = publish.date
        this.scheduleForm.startTime = publish.time
        this.scheduleForm.endDate = expire.date
        this.scheduleForm.endTime = expire.time
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: extractApiErrorMessage(error, 'Unable to load ad for editing'),
          icon: 'error',
        })
        this.$router.push({ name: 'my-ads' }).catch(() => {})
      } finally {
        this.isLoadingAd = false
      }
    },
    onDestinationUrlBlur() {
      this.destinationUrlTouched = true
    },
    onMediaFormatError(message) {
      this.mediaFormatError = message || ''
    },
    onCancel() {
      if (this.embedded) {
        this.$emit('cancel')
        return
      }
      this.$router.push({ name: 'my-ads' }).catch(() => {})
    },
    async goToNextStep() {
      if (this.activeStep === 1) {
        this.showBasicValidation = true
        if (isCategorySuggestValue(this.adForm.categoryId)) {
          const suggestName = String(this.suggestedCategoryName || '').trim()
          if (suggestName) {
            await this.submitCategorySuggestion()
          }
        }
        if (Object.keys(this.buildBasicStepErrors()).length) return
        this.activeStep = 2
        return
      }
      if (this.activeStep === 2) {
        this.showMediaValidation = true
        if (this.mediaFormatError || Object.keys(this.buildMediaStepErrors()).length) return
        this.activeStep = 3
        return
      }
      if (this.activeStep === 3) {
        this.destinationUrlTouched = true
        this.showCtaValidation = true
        if (Object.keys(this.buildCtaStepErrors()).length) return
        this.activeStep = 4
      }
    },
    goToPreviousStep() {
      if (this.activeStep > 1) {
        this.activeStep -= 1
      }
    },
    goToStep(stepId) {
      const targetStep = Number(stepId)
      if (!targetStep || targetStep > this.activeStep) return
      this.activeStep = targetStep
    },
    buildBasicStepErrors() {
      const errors = {}
      if (!this.adForm.title.trim()) errors.title = 'Title is required'
      if (!this.adForm.description.trim()) errors.description = 'Description is required'
      if (!this.adForm.brandId) errors.brandId = 'Brand is required'
      if (isCategorySuggestValue(this.adForm.categoryId)) {
        const suggestName = String(this.suggestedCategoryName || '').trim()
        if (!suggestName) {
          errors.suggestedCategoryName = 'Enter a category name to suggest, or pick an existing category'
        }
        errors.categoryId = 'Send your suggestion to admin, then select an existing primary category for this ad'
      } else if (!this.adForm.categoryId) {
        errors.categoryId = 'Primary category is required'
      }
      if (this.adForm.secondaryCategoryId) {
        const secondaryId = Number(this.adForm.secondaryCategoryId)
        const primaryId = Number(this.adForm.categoryId)
        if (secondaryId === primaryId) {
          errors.secondaryCategoryId = 'Secondary category must differ from primary'
        } else {
          const allowed = secondaryCategoryOptions(this.categoryOptions, primaryId)
            .some(item => Number(item.value) === secondaryId)
          if (!allowed) {
            errors.secondaryCategoryId = 'Secondary category must belong to the selected primary'
          }
        }
      }
      if (this.adForm.minAge === null || this.adForm.minAge === '') {
        errors.minAge = 'Please select an age restriction'
      }
      return errors
    },
    buildMediaStepErrors() {
      const errors = {}
      const imageFormatError = getAdMediaFormatError(this.adForm.mediaFiles, this.adForm.adType)
      if (imageFormatError) {
        errors.media = imageFormatError
      } else if (!this.hasAdMedia()) {
        errors.media = 'Media Upload requires at least one file'
      }
      return errors
    },
    buildCtaStepErrors() {
      const errors = {}
      if (!this.adForm.ctaText.trim()) errors.ctaText = 'CTA button text is required'
      const destinationError = validateDestinationUrl(this.adForm.destinationUrl)
      if (destinationError) errors.destinationUrl = destinationError
      return errors
    },
    buildScheduleFieldErrors() {
      const errors = {}
      if (!this.scheduleForm.startTime) errors.startTime = 'Start time is required'
      if (!this.scheduleForm.endTime) errors.endTime = 'End time is required'
      if (!this.scheduleForm.startDate) errors.startDate = 'Start date is required'
      if (!this.scheduleForm.endDate) errors.endDate = 'End date is required'
      if (
        this.scheduleForm.startDate
        && this.scheduleForm.endDate
        && this.scheduleForm.endDate < this.scheduleForm.startDate
      ) {
        errors.endDate = 'End date cannot be before start date'
      } else {
        const rangeError = validateScheduleRange(this.scheduleForm)
        if (rangeError) errors.scheduleRange = rangeError
      }
      return errors
    },
    onStartDateChange() {
      this.enforceEndDateNotBeforeStart()
    },
    onEndDateChange() {
      this.enforceEndDateNotBeforeStart()
    },
    onStartTimeChange() {
      this.enforceEndTimeNotBeforeStart()
    },
    onEndTimeChange() {
      this.enforceEndTimeNotBeforeStart()
    },
    enforceEndDateNotBeforeStart() {
      const { startDate, endDate } = this.scheduleForm
      if (!startDate || !endDate) return
      if (endDate < startDate) {
        this.scheduleForm.endDate = startDate
      }
      if (startDate === this.scheduleForm.endDate) {
        this.enforceEndTimeNotBeforeStart()
      }
    },
    enforceEndTimeNotBeforeStart() {
      const { startDate, endDate, startTime, endTime } = this.scheduleForm
      if (!startDate || !endDate || startDate !== endDate) return
      if (!startTime || !endTime) return
      if (endTime < startTime) {
        this.scheduleForm.endTime = startTime
      }
    },
    formatTimeAmPm(value) {
      if (!value) return '--:-- --'
      const [hoursRaw = '0', minutesRaw = '0'] = String(value).split(':')
      const hours = Number(hoursRaw)
      const minutes = Number(minutesRaw)
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '--:-- --'
      const period = hours >= 12 ? 'PM' : 'AM'
      const hour12 = hours % 12 || 12
      return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`
    },
    formatDateLong(value) {
      if (!value) return '—'
      const [year, month, day] = String(value).split('-').map(Number)
      if (!year || !month || !day) return '—'
      const date = new Date(year, month - 1, day)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    async submitCreateAd() {
      if (this.submittingCreateAd) return
      this.showScheduleValidation = true
      if (this.hasScheduleErrors) return

      const basicErrors = this.buildBasicStepErrors()
      const mediaErrors = this.buildMediaStepErrors()
      const ctaErrors = this.buildCtaStepErrors()
      if (this.mediaFormatError) mediaErrors.media = this.mediaFormatError

      if (Object.keys(basicErrors).length) {
        this.activeStep = 1
        this.showBasicValidation = true
        return
      }
      if (Object.keys(mediaErrors).length) {
        this.activeStep = 2
        this.showMediaValidation = true
        return
      }
      if (Object.keys(ctaErrors).length) {
        this.activeStep = 3
        this.showCtaValidation = true
        this.destinationUrlTouched = true
        return
      }

      if (isCategorySuggestValue(this.adForm.categoryId)) {
        const suggestName = String(this.suggestedCategoryName || '').trim()
        if (suggestName) {
          await this.submitCategorySuggestion()
        }
        this.activeStep = 1
        this.showBasicValidation = true
        return
      }

      this.submittingCreateAd = true
      const clientRequestId = buildClientRequestId()
      try {
        await vendorPanelApi.submitAdForm({
          mode: this.isEditMode ? 'update' : (this.embedded ? 'submit' : 'draft'),
          adId: this.editAdId,
          existingMediaUrls: this.existingMediaUrls,
          form: this.adForm,
          schedule: this.scheduleForm,
          categories: this.categoryOptions,
          clientRequestId,
        })
        if (this.embedded) {
          this.$emit('submitted')
        } else {
          this.showOutcomeModal = true
        }
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: extractApiErrorMessage(error, 'Unable to create ad right now'),
          icon: 'error',
          timer: 2200,
          showConfirmButton: false,
        })
      } finally {
        this.submittingCreateAd = false
      }
    },
    onCreateAdOutcomePrimary() {
      this.showOutcomeModal = false
      this.$router.push({ name: 'my-ads' }).catch(() => {})
    },
  },
}
</script>

<style lang="scss">
.create-ad-wizard {
  width: 100%;

  &.create-ad-wizard--embedded {
    .create-ad-panel {
      min-height: 560px;
      box-shadow: none;
      border-radius: 0;
    }

    .create-ad-sidebar {
      padding: 1.25rem 1rem 1rem;
    }

    .create-ad-main {
      padding: 1.25rem 1.5rem 1rem;
    }
  }

  .create-ad-panel {
    border: 0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(16, 24, 40, 0.08);
    display: flex;
    flex-direction: row !important;
    align-items: stretch;
    min-height: 720px;
    width: 100%;
  }

  .create-ad-sidebar {
    width: 28%;
    min-width: 280px;
    max-width: 360px;
    flex-shrink: 0;
    background: linear-gradient(180deg, #f3f7ff 0%, #eef4ff 100%);
    border-right: 1px solid #e4ebf8;
    padding: 1.75rem 1.35rem 1.5rem;
    display: flex;
    flex-direction: column;
  }

  .sidebar-header h1 {
    color: #101828;
    font-size: 1.45rem;
    font-weight: 700;
    margin-bottom: 0.45rem;
    line-height: 1.25;
  }

  .sidebar-header .sparkle {
    font-size: 1rem;
  }

  .sidebar-header p {
    margin-bottom: 1.1rem;
    color: #667085;
    font-size: 0.86rem;
    line-height: 1.45;
  }

  .sidebar-illustration {
    display: flex;
    justify-content: center;
    margin-bottom: 1.35rem;
  }

  .illus-card {
    position: relative;
    width: 132px;
    height: 96px;
    border-radius: 14px;
    background: linear-gradient(145deg, #3b82f6 0%, #2563eb 100%);
    box-shadow: 0 10px 24px rgba(37, 99, 235, 0.28);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .illus-ad-label {
    color: #fff;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  .illus-plus {
    position: absolute;
    right: -10px;
    bottom: -10px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #22c55e;
    color: #fff;
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 34px;
    text-align: center;
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.45);
  }

  .create-ad-stepper {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
  }

  .stepper-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.7rem 0.65rem;
    border-radius: 10px;
    margin-bottom: 0.35rem;
    transition: background 0.2s ease;
  }

  .stepper-item.is-clickable {
    cursor: pointer;
  }

  .stepper-item.is-clickable:hover {
    background: rgba(59, 130, 246, 0.08);
  }

  .stepper-item.is-active.is-clickable:hover {
    background: rgba(59, 130, 246, 0.12);
  }

  .stepper-item.is-locked {
    cursor: default;
    opacity: 0.72;
  }

  .stepper-item.is-active {
    background: rgba(59, 130, 246, 0.12);
  }

  .stepper-marker {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #d0d5dd;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .stepper-item.is-active .stepper-marker,
  .stepper-item.is-complete .stepper-marker {
    background: #3b82f6;
  }

  .stepper-copy {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .stepper-copy strong {
    color: #101828;
    font-size: 0.9rem;
    font-weight: 600;
  }

  .stepper-copy span {
    color: #667085;
    font-size: 0.78rem;
    line-height: 1.35;
  }

  .create-ad-main {
    flex: 1;
    min-width: 0;
    padding: 1.5rem 2.25rem 1.25rem;
    background: #fff;
    display: flex;
    flex-direction: column;
  }

  .create-ad-form {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 100%;
  }

  .form-section {
    flex: 0 0 auto;
    margin-bottom: 0;
    padding-bottom: 0.25rem;
  }

  .section-title-row {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 1rem;
  }

  .section-icon-badge {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #3b82f6;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .section-title-row h3 {
    margin: 0;
    color: #101828;
    font-size: 1.05rem;
    font-weight: 700;
  }

  .field-label {
    margin-bottom: 0.35rem;
    color: #344054;
    font-size: 0.88rem;
    font-weight: 600;
    display: block;
  }

  .form-field {
    margin-bottom: 0.85rem;
  }

  .form-field .form-control,
  .form-field .custom-select,
  .input-with-icon .form-control,
  .picker-input-wrap .form-control {
    height: 42px;
    border-color: #d0d5dd;
    border-radius: 8px;
    font-size: 0.92rem;
    color: #344054;
    background: #fff;
  }

  .field-error {
    color: #ea5455;
    font-size: 0.8rem;
    margin: -0.45rem 0 0.65rem;
    line-height: 1.3;
  }

  .category-suggest-block {
    margin: 0.25rem 0 1rem;
    padding: 0.85rem 1rem;
    border: 1px solid #e4ebf8;
    border-radius: 8px;
    background: #f9fbff;
  }

  .field-hint {
    color: #667085;
    font-size: 0.78rem;
    margin: -0.35rem 0 0.85rem;
    line-height: 1.35;
  }

  .form-field.is-invalid .form-control,
  .form-field.is-invalid .custom-select,
  .input-with-icon.is-invalid .form-control,
  .time-date-field.is-invalid .form-control {
    border-color: #ea5455;
  }

  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .input-with-icon {
    position: relative;
    margin-bottom: 0.85rem;
  }

  .input-with-icon .form-control {
    padding-left: 2.35rem;
    margin-bottom: 0;
  }

  .input-icon {
    position: absolute;
    left: 0.85rem;
    top: 50%;
    transform: translateY(-50%);
    color: #98a2b3;
    pointer-events: none;
    z-index: 2;
  }

  .existing-media-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .existing-media-item {
    text-align: center;
  }

  .existing-media-thumb {
    width: 120px;
    height: 120px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #d0d5dd;
    display: block;
  }

  .existing-media-remove {
    margin-top: 0.35rem;
    border: 0;
    background: transparent;
    color: #d9534f;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
  }

  .create-ad-drop-zone.upload-drop-zone {
    border: 2px dashed #b8d4f5;
    border-radius: 10px;
    min-height: 210px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.5rem;
    text-align: center;
    background: #fafcff;
    padding: 1.5rem 1rem;

    h4 {
      color: #344054;
      font-size: 1rem;
      font-weight: 500;
      margin-bottom: 0.55rem;
    }

    h4 .browse-link {
      color: #2563eb;
      font-weight: 600;
    }

    p {
      color: #667085;
      font-size: 0.82rem;
      margin-bottom: 0.2rem;
      line-height: 1.45;
    }

    &.has-files {
      min-height: auto;
      align-items: stretch;
      justify-content: flex-start;
    }
  }

  .schedule-summary-bar {
    background: #eef4ff;
    border: 1px solid #d6e4ff;
    border-radius: 8px;
    color: #101828;
    font-size: 0.95rem;
    font-weight: 600;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
  }

  .time-date-field label {
    margin-bottom: 0.35rem;
    color: #344054;
    font-size: 0.88rem;
    font-weight: 600;
    display: block;
  }

  .picker-input-wrap {
    position: relative;
    margin-bottom: 0.85rem;
  }

  .picker-input-wrap .form-control {
    margin-bottom: 0;
    padding-right: 0.85rem;
  }

  .native-picker-field .form-control {
    padding-right: 0.5rem;
  }

  .form-footer {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid #eaecf0;
  }

  .form-footer--end {
    display: flex;
    justify-content: flex-end;
  }

  .form-footer--split {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .form-footer--three {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    gap: 1rem;
  }

  .back-btn,
  .cancel-btn,
  .next-btn,
  .submit-btn {
    height: 44px;
    border-radius: 10px !important;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
  }

  .back-btn,
  .cancel-btn {
    border-color: #d0d5dd !important;
    color: #344054 !important;
    background: #fff !important;
  }

  .form-footer--end .next-btn {
    min-width: 140px;
  }

  .form-footer--split .back-btn,
  .form-footer--split .next-btn {
    min-width: 140px;
  }

  .next-btn {
    background: #011425 !important;
    border-color: #011425 !important;
    color: #fff !important;
  }

  .cancel-btn {
    width: 100%;
  }

  .submit-btn {
    width: 100%;
    background: #011425 !important;
    border-color: #011425 !important;
    color: #fff !important;
    font-size: 0.95rem;
  }

  .btn-leading-icon,
  .btn-trailing-icon,
  .submit-icon {
    flex-shrink: 0;
  }

  @media (max-width: 991px) {
    .create-ad-panel {
      flex-direction: column;
    }

    .create-ad-sidebar {
      width: 100%;
      min-width: 0;
      max-width: none;
      border-right: 0;
      border-bottom: 1px solid #e4ebf8;
    }

    .create-ad-stepper {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.35rem;
    }
  }

  @media (max-width: 767px) {
    .create-ad-main {
      padding: 1.25rem 1rem;
    }

    .field-row {
      grid-template-columns: 1fr;
    }

    .form-footer--three {
      grid-template-columns: 1fr;
    }

    .form-footer--split {
      flex-direction: column-reverse;
      align-items: stretch;
    }

    .form-footer--split .back-btn,
    .form-footer--split .next-btn,
    .form-footer--end .next-btn {
      width: 100%;
      min-width: 0;
    }

    .create-ad-stepper {
      grid-template-columns: 1fr;
    }
  }
}
</style>
